import type { RadioTransport } from './web-serial.ts'

export const TIDRADIO_HID_FILTER = {
  vendorId: 0x15a2,
  productId: 0x0073,
} as const

type HidReportInfo = {
  reportId: number
  items?: Array<{
    reportSize: number
    reportCount: number
  }>
}

type HidCollection = {
  inputReports?: HidReportInfo[]
  outputReports?: HidReportInfo[]
}

type HidInputReportEvent = Event & {
  reportId: number
  data: DataView
}

export type WebHidDevice = EventTarget & {
  readonly opened: boolean
  readonly collections: HidCollection[]
  open(): Promise<void>
  close(): Promise<void>
  sendReport(reportId: number, data: BufferSource): Promise<void>
}

type HidNavigator = Navigator & {
  hid?: {
    requestDevice(options: {
      filters: Array<{ vendorId?: number; productId?: number }>
    }): Promise<WebHidDevice[]>
    getDevices(): Promise<WebHidDevice[]>
  }
}

function reportLength(report: HidReportInfo | undefined): number {
  if (!report?.items?.length) return 0
  return report.items.reduce(
    (length, item) => length + item.reportSize * item.reportCount,
    0,
  )
}

export function hasWebHid(): boolean {
  return typeof navigator !== 'undefined' && Boolean((navigator as HidNavigator).hid)
}

export async function requestWebHidDevice(
  filters: Array<{ vendorId?: number; productId?: number }> = [
    TIDRADIO_HID_FILTER,
  ],
): Promise<WebHidDevice> {
  const hid = (navigator as HidNavigator).hid
  if (!hid) {
    throw new Error(
      'WebHID is not available in this browser. Use desktop Chrome, Edge, or another Chromium browser over HTTPS.',
    )
  }
  const [device] = await hid.requestDevice({ filters })
  if (!device) throw new Error('No compatible USB HID programming interface was selected.')
  return device
}

export class WebHidRadioTransport implements RadioTransport {
  private readonly device: WebHidDevice
  private inputBuffer = new Uint8Array(0)
  private waiters = new Set<() => void>()
  private failure: Error | null = null
  private reportId = 0
  private outputLength = 64
  private opened = false

  constructor(device: WebHidDevice) {
    this.device = device
  }

  async open(): Promise<void> {
    if (!this.opened) {
      await this.device.open()
      this.opened = true
    }
    const outputReports = this.device.collections.flatMap(
      (collection) => collection.outputReports ?? [],
    )
    const inputReports = this.device.collections.flatMap(
      (collection) => collection.inputReports ?? [],
    )
    const output =
      outputReports.find((report) => report.reportId === 1) ??
      outputReports.find((report) => report.reportId === 0) ??
      outputReports[0]
    const input =
      inputReports.find((report) => report.reportId === output?.reportId) ??
      inputReports.find((report) => report.reportId === 1) ??
      inputReports[0]
    this.reportId = output?.reportId ?? input?.reportId ?? 0
    this.outputLength = Math.max(16, reportLength(output), reportLength(input), 64)
    this.device.addEventListener('inputreport', this.onInputReport)
  }

  private readonly onInputReport = (event: Event): void => {
    const report = event as HidInputReportEvent
    if (report.reportId !== this.reportId && this.reportId !== 0) return
    const raw = new Uint8Array(
      report.data.buffer,
      report.data.byteOffset,
      report.data.byteLength,
    )
    let payloadStart = 0
    let payloadLength = raw.byteLength
    if (raw.byteLength >= 3 && raw[0] === 0) {
      payloadStart = 3
      payloadLength = Math.min(raw[1] | (raw[2] << 8), raw.byteLength - 3)
    } else if (raw.byteLength >= 4 && raw[1] === 0) {
      payloadStart = 4
      payloadLength = Math.min(raw[2] | (raw[3] << 8), raw.byteLength - 4)
    }
    if (!payloadLength) return
    const payload = raw.slice(payloadStart, payloadStart + payloadLength)
    const next = new Uint8Array(this.inputBuffer.byteLength + payload.byteLength)
    next.set(this.inputBuffer)
    next.set(payload, this.inputBuffer.byteLength)
    this.inputBuffer = next
    this.notifyWaiters()
  }

  private notifyWaiters(): void {
    for (const waiter of this.waiters) waiter()
    this.waiters.clear()
  }

  private waitForData(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        this.waiters.delete(finish)
        resolve()
      }
      const timer = setTimeout(finish, timeoutMs)
      this.waiters.add(finish)
    })
  }

  async close(): Promise<void> {
    if (!this.opened) return
    this.opened = false
    this.device.removeEventListener('inputreport', this.onInputReport)
    this.inputBuffer = new Uint8Array(0)
    this.waiters.clear()
    await this.device.close()
  }

  async discardInput(): Promise<void> {
    this.inputBuffer = new Uint8Array(0)
    this.failure = null
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.opened) throw new Error('Open the USB HID programming interface first.')
    const maxPayload = Math.max(1, this.outputLength - 3)
    if (data.byteLength > maxPayload) {
      throw new Error(
        `The USB HID frame is ${data.byteLength} bytes; this interface accepts ${maxPayload}.`,
      )
    }
    const report = new Uint8Array(this.outputLength)
    report[0] = 0
    report[1] = data.byteLength & 0xff
    report[2] = (data.byteLength >> 8) & 0xff
    report.set(data, 3)
    await this.device.sendReport(this.reportId, report)
  }

  async readExact(size: number, timeoutMs = 2500): Promise<Uint8Array> {
    if (size < 0) throw new Error('Read size cannot be negative.')
    const deadline = Date.now() + timeoutMs
    while (this.inputBuffer.byteLength < size) {
      if (this.failure) throw this.failure
      const remaining = deadline - Date.now()
      if (remaining <= 0) {
        throw new Error(
          `USB HID read timed out waiting for ${size} bytes; received ${this.inputBuffer.byteLength}.`,
        )
      }
      await this.waitForData(Math.min(remaining, 100))
    }
    const result = this.inputBuffer.slice(0, size)
    this.inputBuffer = this.inputBuffer.slice(size)
    return result
  }
}
